/*
 * The translation machinery. `t()` is typed over a catalogue: a key that does not exist is a
 * compile error, and so is a missing placeholder — which is what makes "no literal in JSX"
 * (react/jsx-no-literals, Q38) affordable to enforce.
 *
 * Only the machinery lives here, because more than one catalogue needs it: the application's
 * merged catalogue (`core/i18n/index.ts`, which is what every screen imports) and a frontend
 * module's own namespace, whose keys arrive with its route bundle and are therefore not part of the
 * application's `Key` union. A second copy of the placeholder typing would drift silently — both
 * sides would keep compiling — so a core area's own `i18n/t.ts` is `makeT(en)` and nothing else,
 * and a catalogue that arrives with a chunk of its own — a module's, or the document's
 * (`core/docs/i18n`, namespace `doc`) — is `namespaceT<typeof en>(id)`.
 */

import { LOCALE } from "./format.ts"

/** The `{name}` placeholders of one catalogue entry, as a union of their names. */
export type Placeholders<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
	? P | Placeholders<Rest>
	: never

/** The arguments one entry of a catalogue needs: one per placeholder in its text, and no others. */
export type ArgsOf<C, K extends keyof C> = Record<Placeholders<C[K] & string>, string | number>

/** Empty for an entry without placeholders, so `t(key)` needs no second argument there. */
export type ArgsParam<C, K extends keyof C> = [Placeholders<C[K] & string>] extends [never]
	? []
	: [args: ArgsOf<C, K>]

/**
 * The plural forms of the interface's language, as Intl names them ("one", "other", and in other
 * languages "few" and "many" as well).
 */
const plural = new Intl.PluralRules(LOCALE)

/**
 * The text of one key for the count it is about. English has two forms and the interface says
 * "{count} members" in half a dozen places, where one member read "1 members".
 *
 * A catalogue opts in per key by carrying a sibling `<key>.one` (or `.few`, `.many` — the category
 * names are Intl's): whenever the caller passes a numeric `count`, the sibling for that count's
 * category is used if the catalogue has one, and the plain key otherwise. Nothing changes for a key
 * without siblings, and the typing of `t` is untouched — a sibling is an ordinary catalogue entry.
 */
function templateFor(
	lookup: Lookup,
	key: string,
	args: Record<string, string | number> | undefined,
): string {
	const sibling = pluralKey(key, args)
	return (sibling === key ? undefined : lookup(sibling)) ?? lookup(key) ?? missing(key)
}

/**
 * The sibling a key has for the count it was given, or the key itself. It is exported because the
 * rule belongs to the *catalogue* and not to one accessor: a key that arrived at runtime — a
 * module's namespace, a sentence the server registered — is rendered by `translate`, and read "1
 * changes discarded." for as long as the two accessors knew different rules.
 */
export function pluralKey(key: string, args?: Record<string, string | number>): string {
	const count = args?.["count"]
	return typeof count === "number" ? `${key}.${plural.select(count)}` : key
}

/**
 * What a key nobody ships renders as: its last segment, as a word.
 *
 * `tsc` is the gate for a dangling key and it cannot be got past — but the runtime that arrives here
 * anyway used to be handed `undefined`, and `emojiText` called `.split` on it, which took **every
 * conversation route** down to the router's error boundary because one tooltip named a key the
 * catalogue did not carry yet (X-B4 B4-17, K-07's incident). A missing key costs a word, not a
 * screen. The warning belongs to the development build: the person looking at that console is the
 * one who can fix the key, and in production it would be noise in a console nobody opens.
 */
function missing(key: string): string {
	if (import.meta.env.DEV) console.warn(`i18n: no entry for "${key}"`)
	return key.slice(key.lastIndexOf(".") + 1)
}

/** Fills `{name}` placeholders; an argument the text does not name is ignored, not an error. */
export function interpolate(template: string, args: Record<string, string | number>): string {
	return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
		name in args ? String(args[name]) : whole,
	)
}

/** Where an accessor finds the text of a key: `undefined` for a key it does not hold. */
type Lookup = (key: string) => string | undefined

/**
 * The typed accessor of one catalogue: `t(key)` for a text without placeholders, `t(key, args)` for
 * one with them, and a compile error for anything else.
 */
export function makeT<C extends Record<string, string>>(catalogue: C) {
	const entries: Record<string, string> = catalogue
	return makeLookupT<C>((key) => entries[key])
}

/**
 * The same accessor over a catalogue that is known here only as a type, its text looked up when a
 * key is rendered — which is what lets a module's copy arrive in its own chunk (`namespaceT` in
 * `./index.ts`).
 */
export function makeLookupT<C extends Record<string, string>>(lookup: Lookup) {
	return <K extends keyof C & string>(key: K, ...rest: ArgsParam<C, K>): string => {
		const args = rest[0] as Record<string, string | number> | undefined
		const template = templateFor(lookup, key, args)
		return args ? interpolate(template, args) : template
	}
}
